import { useApi } from "@platform/api";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

/** How often to ask again while waiting for a census to be read. */
const POLL_WHILE_READING_MS = 3000;

/**
 * How long to keep asking before giving up.
 *
 * A read takes seconds. Past this the workflow is not coming back — it died
 * before recording anything, or never started — and asking forever would
 * leave the page claiming it is about to update when it never will.
 */
const GIVE_UP_AFTER_MS = 120_000;

/** HTTP 204: the read asked about has not happened. */
const NOT_READ_YET = 204;

export interface CensusReadStatus {
  ingestionId: number;
  source: string;
  outcome: string;
  readAt?: string;
}

/**
 * An upload waiting to be read.
 *
 * Identified by its own document rather than by the read the page was
 * showing: two uploads can be outstanding at once, and a broker's own read
 * can land between them, so "something newer appeared" does not mean "mine
 * was read".
 */
export interface AwaitedRead {
  readonly documentId: number;
  readonly startedAt: number;
}

interface UseCensusReadStatusReturn {
  /** The read of the awaited document, once it exists. */
  status?: CensusReadStatus;
  /** True once the wait ran out without that document being read. */
  gaveUp: boolean;
}

/** Whether a wait has run past the point a read could still be coming. */
export const waitedLongEnough = (
  awaiting: AwaitedRead | undefined,
  now: number,
): boolean => !!awaiting && now - awaiting.startedAt >= GIVE_UP_AFTER_MS;

/**
 * Re-renders once a wait's deadline passes.
 *
 * The deadline cannot be noticed by rendering alone: while every poll returns
 * the same answer React Query has no new data to publish, so nothing would
 * re-render to observe it.
 */
const useDeadline = (awaiting: AwaitedRead | undefined): boolean => {
  const [reached, setReached] = useState(false);

  useEffect(() => {
    setReached(false);

    if (!awaiting) {
      return;
    }

    const remaining = awaiting.startedAt + GIVE_UP_AFTER_MS - Date.now();
    if (remaining <= 0) {
      setReached(true);

      return;
    }

    const timer = setTimeout(() => setReached(true), remaining);

    return () => clearTimeout(timer);
  }, [awaiting]);

  return reached;
};

/**
 * Whether an upload has been read yet.
 *
 * Small and polled, unlike the validation payload: it answers only whether
 * this document has been read and what the read made of it. A read that
 * produced no census is visible here and nowhere else.
 */
export function useCensusReadStatus(
  rfpId: string,
  awaiting?: AwaitedRead,
): UseCensusReadStatusReturn {
  const axiosInstance = useApi();
  const deadlineReached = useDeadline(awaiting);

  const query = useQuery({
    queryKey: ["censusReadStatus", rfpId, awaiting?.documentId],
    queryFn: async () => {
      const response = await axiosInstance.get<CensusReadStatus | "">(
        `/bp/v1/rfps/${rfpId}/census-read-status`,
        awaiting ? { params: { documentId: awaiting.documentId } } : undefined,
      );

      return response.status === NOT_READ_YET || !response.data
        ? null
        : response.data;
    },
    enabled: !!rfpId,

    // Polling is the only way to see an asynchronous read finish, so it is
    // bounded at both ends: it stops when this document's read appears, and
    // it stops when one plainly is not coming.
    refetchInterval: (query) =>
      awaiting && !query.state.data && !deadlineReached
        ? POLL_WHILE_READING_MS
        : false,
  });

  return {
    status: query.data ?? undefined,
    gaveUp: deadlineReached && !query.data,
  };
}

export { GIVE_UP_AFTER_MS, POLL_WHILE_READING_MS };
